import { useCallback, useEffect, useState, type ReactNode } from 'react'

import { AuditOutlined, RobotOutlined, SettingOutlined } from '@ant-design/icons'

import {
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Space,
  Switch,
  Tag,
  Typography,
  message,
} from 'antd'

import { adminApi } from '../../api'

import { PageHeader } from '../../components/ui'

import {
  AiScoringRules,
  ExtractionRules,
  IndexHealthRules,
  QualityFormulaSummary,
  QualityRuleAside,
  RedundancyRules,
  StructureRules,
  TierRules,
  ruleDimensionWeightShare,
  weightLabel,
} from './qualityRulesHelp'

import type { QualityCfg } from './qualitySettingsTypes'

import './QualitySettings.css'

const VETO_OPTIONS = [
  { value: 'empty_body', label: '正文为空' },
  { value: 'extract_failed', label: '解析失败' },
  { value: 'index_empty', label: '无有效切片' },
]

const RULE_DIM_KEYS = ['extraction', 'structure', 'index_health'] as const

const DIM_LABELS: Record<(typeof RULE_DIM_KEYS)[number], string> = {
  extraction: '正文是否可读',
  structure: '篇幅是否充实',
  index_health: '索引是否正常',
}

const DIM_RULES: Record<(typeof RULE_DIM_KEYS)[number], (props: { dimensions: QualityCfg['dimensions'] | undefined }) => ReactNode> = {
  extraction: ExtractionRules,
  structure: StructureRules,
  index_health: () => <IndexHealthRules />,
}

function snapshotValues(form: ReturnType<typeof Form.useForm<QualityCfg>>[0]) {
  return form.getFieldsValue(true) as QualityCfg
}

function RuleConfigSection({
  title,
  extra,
  rule,
  config,
}: {
  title: string
  extra?: ReactNode
  rule: ReactNode
  config: ReactNode
}) {
  return (
    <Card size="small" className="quality-section-card" title={title} extra={extra}>
      <Row gutter={[24, 16]} className="quality-section-row">
        <Col xs={24} lg={10}>
          <QualityRuleAside>{rule}</QualityRuleAside>
        </Col>
        <Col xs={24} lg={14}>
          <div className="quality-section-config">{config}</div>
        </Col>
      </Row>
    </Card>
  )
}

export default function CleaningSettings() {
  const [form] = Form.useForm<QualityCfg>()
  const [loading, setLoading] = useState(false)
  const [savedSnapshot, setSavedSnapshot] = useState<QualityCfg | null>(null)

  const loadConfig = useCallback(() => {
    return adminApi.get<QualityCfg>('/admin/settings/quality').then((r) => {
      form.setFieldsValue(r.data)
      setSavedSnapshot(r.data)
    })
  }, [form])

  useEffect(() => {
    void loadConfig().catch((e) => message.error(e.message))
  }, [loadConfig])

  const qualityEnabled = Form.useWatch('enabled', form) ?? false
  const autoOnIndex = Form.useWatch('auto_on_index', form) ?? false
  const useLlm = Form.useWatch('use_llm', form) ?? false
  const dimensions = Form.useWatch('dimensions', form)
  const tiers = Form.useWatch('tiers', form)
  const redundancy = Form.useWatch('redundancy', form)

  const savedEnabled = savedSnapshot?.enabled ?? false
  const configDisabled = !qualityEnabled

  const [dirty, setDirty] = useState(false)

  const syncDirty = useCallback(() => {
    if (!savedSnapshot) {
      setDirty(false)
      return
    }
    setDirty(JSON.stringify(snapshotValues(form)) !== JSON.stringify(savedSnapshot))
  }, [form, savedSnapshot])

  useEffect(() => {
    syncDirty()
  }, [syncDirty, qualityEnabled, autoOnIndex, useLlm, dimensions, tiers, redundancy])

  const persistConfig = (values: QualityCfg, successMessage: string) => {
    setLoading(true)
    return adminApi
      .put('/admin/settings/quality', values)
      .then((r) => {
        message.success(successMessage)
        const next = (r.data || values) as QualityCfg
        form.setFieldsValue(next)
        setSavedSnapshot(next)
        setDirty(false)
      })
      .catch((e) => message.error(e.message))
      .finally(() => setLoading(false))
  }

  const onMasterSwitchChange = (checked: boolean) => {
    if (checked) return
    Modal.confirm({
      title: '关闭文档质量评估？',
      content: '关闭后，新入库文档将不再自动打质量标签。已有标签保留，下方配置仍会保存，随时可重新开启。',
      okText: '确认关闭',
      cancelText: '取消',
      onOk: () => message.info('请点击底部「保存配置」使关闭生效'),
      onCancel: () => form.setFieldValue('enabled', true),
    })
  }

  const reset = () => {
    if (!savedSnapshot) return
    form.setFieldsValue(savedSnapshot)
    setDirty(false)
    message.info('已恢复为上次保存的配置')
  }

  const save = () => {
    void form.validateFields().then((v) => {
      const msg = v.enabled ? '配置已保存' : '已关闭文档质量评估，配置已保存'
      void persistConfig(v, msg)
    })
  }

  const dimEnabled = (key: (typeof RULE_DIM_KEYS)[number]) => dimensions?.[key]?.enabled !== false

  return (
    <div className="quality-settings">
      <PageHeader
        title="文档质量评估"
        description="程序规则与 AI 评分二选一。每种方式下，左侧为规则说明、右侧为可改参数。"
      />

      <Form form={form} layout="vertical" className="quality-settings-form" onValuesChange={() => syncDirty()}>
        <Card
          className="quality-card"
          title={
            <Space>
              <AuditOutlined />
              常用开关
              {savedSnapshot ? (
                <Tag color={savedEnabled ? 'success' : 'default'}>{savedEnabled ? '运行中' : '已关闭'}</Tag>
              ) : null}
            </Space>
          }
        >
          <div className="quality-switch-grid quality-switch-grid--pair">
            <div className={`quality-switch-tile${qualityEnabled ? ' quality-switch-tile--active' : ''}`}>
              <div className="quality-switch-head">
                <Typography.Text strong>启用文档质量评估</Typography.Text>
                <Form.Item name="enabled" valuePropName="checked" noStyle>
                  <Switch onChange={onMasterSwitchChange} />
                </Form.Item>
              </div>
              <Typography.Text type="secondary" className="quality-switch-desc">
                总开关。关闭后不再评估新文档。
              </Typography.Text>
              {qualityEnabled ? (
                <div className="quality-mode-inline">
                  <Typography.Text type="secondary" className="quality-mode-inline-label">
                    评分方式
                  </Typography.Text>
                  <Form.Item name="use_llm" noStyle>
                    <Radio.Group className="quality-mode-radio quality-mode-radio--inline">
                      <Radio value={false}>程序规则</Radio>
                      <Radio value={true}>AI 评分</Radio>
                    </Radio.Group>
                  </Form.Item>
                </div>
              ) : null}
            </div>

            <div
              className={`quality-switch-tile${!configDisabled && autoOnIndex ? ' quality-switch-tile--active' : ''}${configDisabled ? ' quality-switch-tile--muted' : ''}`}
            >
              <div className="quality-switch-head">
                <Typography.Text strong>索引完成后自动评估</Typography.Text>
                <Form.Item name="auto_on_index" valuePropName="checked" noStyle>
                  <Switch disabled={configDisabled} />
                </Form.Item>
              </div>
              <Typography.Text type="secondary" className="quality-switch-desc">
                推荐开启。新文档入库后自动打标签。
              </Typography.Text>
            </div>
          </div>
        </Card>

        <fieldset disabled={configDisabled} className="quality-config-fieldset">
          <div className={configDisabled ? 'quality-panel quality-panel--muted' : 'quality-panel'}>
            <Card size="small" className="quality-section-card" title="评分流程">
              <QualityFormulaSummary dimensions={dimensions} tiers={tiers} useLlm={useLlm} />
            </Card>

            {useLlm ? (
              <>
                <Typography.Title level={5} className="quality-block-title">
                  <RobotOutlined /> AI 评审
                </Typography.Title>
                <RuleConfigSection
                  title="AI 如何打分"
                  rule={<AiScoringRules tiers={tiers} />}
                  config={
                    <>
                      <Form.Item
                        name="llm_criteria"
                        label="评审标准"
                        extra="写入 AI 系统提示词，告诉模型从哪些角度评判质量"
                      >
                        <Input.TextArea rows={5} placeholder="每行一条评审要点" />
                      </Form.Item>
                      <Row gutter={[16, 0]}>
                        <Col xs={24} sm={12}>
                          <Form.Item name={['tiers', 'high_min']} label="高质量下限">
                            <InputNumber min={0} max={100} addonAfter="分" className="quality-input-full" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item name={['tiers', 'medium_min']} label="中质量下限">
                            <InputNumber min={0} max={100} addonAfter="分" className="quality-input-full" />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.Item
                        name="llm_system_prompt"
                        label="补充说明（可选）"
                        extra="追加到提示词末尾，用于强调行业或场景特殊要求"
                      >
                        <Input.TextArea rows={3} placeholder="留空则仅使用上方评审标准与分档阈值" />
                      </Form.Item>
                      <Form.Item name="veto_rules" label="一票否决（直接标低）" className="quality-veto-group">
                        <Checkbox.Group options={VETO_OPTIONS} />
                      </Form.Item>
                    </>
                  }
                />
              </>
            ) : (
              <>
                <Typography.Title level={5} className="quality-block-title">
                  <SettingOutlined /> 程序规则维度
                </Typography.Title>
                {RULE_DIM_KEYS.map((key) => {
                  const RuleComp = DIM_RULES[key]
                  const share = ruleDimensionWeightShare(dimensions, key)
                  const weightHint = weightLabel(dimensions?.[key]?.weight, share)
                  return (
                    <RuleConfigSection
                      key={key}
                      title={DIM_LABELS[key]}
                      extra={
                        <Space size={8}>
                          <Tag>{weightHint}</Tag>
                          <Form.Item name={['dimensions', key, 'enabled']} valuePropName="checked" noStyle>
                            <Switch size="small" />
                          </Form.Item>
                        </Space>
                      }
                      rule={<RuleComp dimensions={dimensions} />}
                      config={
                        <Row gutter={[16, 0]}>
                          <Col xs={24} sm={12}>
                            <Form.Item name={['dimensions', key, 'weight']} label="权重" extra={weightHint}>
                              <InputNumber
                                min={0}
                                max={1}
                                step={0.05}
                                className="quality-input-full"
                                disabled={!dimEnabled(key)}
                              />
                            </Form.Item>
                          </Col>
                          {key === 'extraction' ? (
                            <>
                              <Col xs={24} sm={12}>
                                <Form.Item name={['dimensions', key, 'min_chars']} label="最少有效字数">
                                  <InputNumber min={0} max={50000} addonAfter="字" className="quality-input-full" disabled={!dimEnabled(key)} />
                                </Form.Item>
                              </Col>
                              <Col xs={24} sm={12}>
                                <Form.Item name={['dimensions', key, 'garble_max']} label="乱码比例上限">
                                  <InputNumber min={0} max={1} step={0.05} className="quality-input-full" disabled={!dimEnabled(key)} />
                                </Form.Item>
                              </Col>
                            </>
                          ) : null}
                          {key === 'structure' ? (
                            <>
                              <Col xs={24} sm={12}>
                                <Form.Item name={['dimensions', key, 'thin_chars']} label="偏薄篇幅线">
                                  <InputNumber min={0} max={50000} addonAfter="字" className="quality-input-full" disabled={!dimEnabled(key)} />
                                </Form.Item>
                              </Col>
                              <Col xs={24} sm={12}>
                                <Form.Item name={['dimensions', key, 'rich_chars']} label="充实篇幅线">
                                  <InputNumber min={0} max={500000} addonAfter="字" className="quality-input-full" disabled={!dimEnabled(key)} />
                                </Form.Item>
                              </Col>
                            </>
                          ) : null}
                        </Row>
                      }
                    />
                  )
                })}

                <Typography.Title level={5} className="quality-block-title">
                  <SettingOutlined /> 质量分档
                </Typography.Title>
                <RuleConfigSection
                  title="高 / 中 / 低 如何划分"
                  rule={<TierRules tiers={tiers} />}
                  config={
                    <>
                      <Row gutter={[16, 0]}>
                        <Col xs={24} sm={12}>
                          <Form.Item name={['tiers', 'high_min']} label="高质量下限">
                            <InputNumber min={0} max={100} addonAfter="分" className="quality-input-full" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item name={['tiers', 'medium_min']} label="中质量下限">
                            <InputNumber min={0} max={100} addonAfter="分" className="quality-input-full" />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.Item name="veto_rules" label="一票否决（直接标低）" className="quality-veto-group">
                        <Checkbox.Group options={VETO_OPTIONS} />
                      </Form.Item>
                    </>
                  }
                />
              </>
            )}

            <Typography.Title level={5} className="quality-block-title">
              <SettingOutlined /> 重复检测
            </Typography.Title>
            <RuleConfigSection
              title="重复文档判定"
              rule={<RedundancyRules redundancy={redundancy} />}
              config={
                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={8}>
                    <Form.Item name={['redundancy', 'light_min']} label="轻度重复下限">
                      <InputNumber min={0} max={1} step={0.05} className="quality-input-full" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item name={['redundancy', 'heavy_min']} label="高度重复下限">
                      <InputNumber min={0} max={1} step={0.05} className="quality-input-full" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item name={['redundancy', 'sample_limit']} label="抽样比对篇数">
                      <InputNumber min={5} max={100} addonAfter="篇" className="quality-input-full" />
                    </Form.Item>
                  </Col>
                </Row>
              }
            />
          </div>
        </fieldset>

        <div className="quality-settings-footer">
          <Space size="middle" className="quality-settings-footer-inner">
            {dirty ? (
              <Typography.Text type="warning" className="quality-dirty-hint">
                有未保存的更改
              </Typography.Text>
            ) : (
              <Typography.Text type="secondary" className="quality-dirty-hint">
                所有更改需保存后生效
              </Typography.Text>
            )}
            <Space>
              <Button disabled={!dirty || loading} onClick={reset}>
                重置
              </Button>
              <Button type="primary" loading={loading} disabled={!dirty} onClick={save}>
                保存配置
              </Button>
            </Space>
          </Space>
        </div>
      </Form>
    </div>
  )
}
